export type MaltaFullTimeFeeLineKey =
  | "tuitionFee"
  | "acommodation"
  | "medicalInsurance"
  | "residencePermit"
  | "admissionFee"
  | "otherExpenses"
  | "transfer"
  | "certificationEqualization"
  | "completeOneYear";

export type MaltaFullTimeFeeVisibility = Record<MaltaFullTimeFeeLineKey, boolean>;

export type MaltaFullTimeNoticeKey =
  | "transferFeeNotice"
  | "admissionLetterNotice"
  | "firstYearPaymentConfirmation";

export const MALTA_FULL_TIME_FEE_LINE_ORDER: MaltaFullTimeFeeLineKey[] = [
  "tuitionFee",
  "acommodation",
  "medicalInsurance",
  "residencePermit",
  "admissionFee",
  "otherExpenses",
  "transfer",
  "certificationEqualization",
  "completeOneYear",
];

export const MALTA_FULL_TIME_FEE_VISIBILITY_FIELD: Record<MaltaFullTimeFeeLineKey, keyof MaltaFullTimeFeeVisibilityFormKeys> = {
  tuitionFee: "tuitionFeeVisible",
  acommodation: "acommodationVisible",
  medicalInsurance: "medicalInsuranceVisible",
  residencePermit: "residencePermitVisible",
  admissionFee: "admissionFeeVisible",
  otherExpenses: "otherExpensesVisible",
  transfer: "transferVisible",
  certificationEqualization: "certificationEqualizationVisible",
  completeOneYear: "completeOneYearVisible",
};

export interface MaltaFullTimeFeeVisibilityFormKeys {
  tuitionFeeVisible: boolean;
  acommodationVisible: boolean;
  medicalInsuranceVisible: boolean;
  residencePermitVisible: boolean;
  admissionFeeVisible: boolean;
  otherExpensesVisible: boolean;
  transferVisible: boolean;
  certificationEqualizationVisible: boolean;
  completeOneYearVisible: boolean;
}

export const MALTA_FULL_TIME_ON_CAMPUS_FEE_VISIBILITY: MaltaFullTimeFeeVisibility = {
  tuitionFee: true,
  acommodation: true,
  medicalInsurance: true,
  residencePermit: true,
  admissionFee: true,
  otherExpenses: true,
  transfer: true,
  certificationEqualization: false,
  completeOneYear: true,
};

export const MALTA_FULL_TIME_ON_CAMPUS_FEE_VALUES: Record<MaltaFullTimeFeeLineKey, string> = {
  tuitionFee: "",
  acommodation: "1 200",
  medicalInsurance: "",
  residencePermit: "200",
  admissionFee: "1 000",
  otherExpenses: "800",
  transfer: "",
  certificationEqualization: "",
  completeOneYear: "",
};

export const MALTA_FORM_OF_EDUCATION_ALL_ACTIVE_FEE_VISIBILITY: MaltaFullTimeFeeVisibility = {
  tuitionFee: true,
  acommodation: true,
  medicalInsurance: true,
  residencePermit: true,
  admissionFee: true,
  otherExpenses: true,
  transfer: true,
  certificationEqualization: true,
  completeOneYear: true,
};

export const MALTA_FORM_OF_EDUCATION_ALL_ACTIVE_NOTICE_DEFAULTS = {
  showSecondYear: true,
  showTransferFeeNotice: true,
  showAdmissionLetterNotice: true,
  showFirstYearPaymentConfirmation: true,
} as const;

/** @deprecated Use MALTA_FULL_TIME_ON_CAMPUS_FEE_VISIBILITY for Full-time defaults. */
export const DEFAULT_MALTA_FULL_TIME_FEE_VISIBILITY = MALTA_FULL_TIME_ON_CAMPUS_FEE_VISIBILITY;

/** @deprecated Use MALTA_FULL_TIME_ON_CAMPUS_FEE_VALUES for Full-time defaults. */
export const DEFAULT_MALTA_FULL_TIME_FEE_VALUES = MALTA_FULL_TIME_ON_CAMPUS_FEE_VALUES;

export const MALTA_FULL_TIME_ON_CAMPUS_NOTICE_DEFAULTS = {
  showSecondYear: true,
  showTransferFeeNotice: false,
  showAdmissionLetterNotice: false,
  showFirstYearPaymentConfirmation: false,
} as const;

type SetMaltaFullTimeFormValue = (
  name: string,
  value: string | boolean,
  options?: { shouldDirty?: boolean },
) => void;

export const applyMaltaFullTimeOnCampusFormDefaults = (setValue: SetMaltaFullTimeFormValue): void => {
  (Object.entries(MALTA_FULL_TIME_ON_CAMPUS_FEE_VALUES) as Array<[MaltaFullTimeFeeLineKey, string]>).forEach(
    ([key, value]) => {
      setValue(key, value, { shouldDirty: false });
    },
  );

  (Object.entries(MALTA_FULL_TIME_ON_CAMPUS_FEE_VISIBILITY) as Array<[MaltaFullTimeFeeLineKey, boolean]>).forEach(
    ([key, visible]) => {
      setValue(MALTA_FULL_TIME_FEE_VISIBILITY_FIELD[key], visible, { shouldDirty: false });
    },
  );

  Object.entries(MALTA_FULL_TIME_ON_CAMPUS_NOTICE_DEFAULTS).forEach(([key, value]) => {
    setValue(key, value, { shouldDirty: false });
  });
};

export const applyMaltaFormOfEducationUnselectedDefaults = (setValue: SetMaltaFullTimeFormValue): void => {
  (Object.keys(MALTA_FULL_TIME_ON_CAMPUS_FEE_VALUES) as MaltaFullTimeFeeLineKey[]).forEach((key) => {
    setValue(key, "", { shouldDirty: false });
  });

  (Object.entries(MALTA_FORM_OF_EDUCATION_ALL_ACTIVE_FEE_VISIBILITY) as Array<[MaltaFullTimeFeeLineKey, boolean]>).forEach(
    ([key, visible]) => {
      setValue(MALTA_FULL_TIME_FEE_VISIBILITY_FIELD[key], visible, { shouldDirty: false });
    },
  );

  Object.entries(MALTA_FORM_OF_EDUCATION_ALL_ACTIVE_NOTICE_DEFAULTS).forEach(([key, value]) => {
    setValue(key, value, { shouldDirty: false });
  });
};

export const MALTA_FULL_TIME_FEE_LINE_SUFFIXES: Record<MaltaFullTimeFeeLineKey, string> = {
  tuitionFee: " EUR every year",
  acommodation: " EUR every year",
  medicalInsurance: " EUR (100 EUR per a year) one-time payment",
  residencePermit: " EUR one-time payment",
  admissionFee: " EUR one-time payment",
  otherExpenses: " EUR first year only",
  transfer: " EUR one-time payment",
  certificationEqualization: " EUR (first year only)",
  completeOneYear: " EUR per year",
};

export const formatMaltaFullTimeFeeLineDisplay = (key: MaltaFullTimeFeeLineKey, numericValue?: string) => {
  const trimmed = numericValue?.trim();
  if (!trimmed) return "";
  return `${trimmed}${MALTA_FULL_TIME_FEE_LINE_SUFFIXES[key]}`;
};

export const MALTA_FULL_TIME_FEE_LINE_LABELS: Record<MaltaFullTimeFeeLineKey, string> = {
  tuitionFee: "Tuition Fee",
  acommodation: "Accommodation",
  medicalInsurance: "Medical Insurance",
  residencePermit: "Temporary Residence Permission and Medical Check-Up",
  admissionFee: "Admission Fee",
  otherExpenses: "Other Expenses (Administrative Fee, Registration Fee, Application Fee, etc.)",
  transfer: "Transfer Charges",
  certificationEqualization: "Certification and Equalization of all documents",
  completeOneYear: "Complete first year expenses",
};

export const MALTA_FULL_TIME_NOTICE_ORDER: MaltaFullTimeNoticeKey[] = [
  "transferFeeNotice",
  "admissionLetterNotice",
  "firstYearPaymentConfirmation",
];

export const MALTA_FULL_TIME_NOTICE_LABELS: Record<MaltaFullTimeNoticeKey, string> = {
  transferFeeNotice: "Transfer Fee Notice",
  admissionLetterNotice: "Admission Letter Notice",
  firstYearPaymentConfirmation: "First-Year Payment Confirmation",
};

export const MALTA_FULL_TIME_NOTICE_TEXT: Record<MaltaFullTimeNoticeKey, string> = {
  transferFeeNotice:
    "Kindly note that since you are starting from the upper year the transfer fee which is based on your academic difference will be calculated on your arrival and might be requested additionally from your side.",
  admissionLetterNotice:
    "This Admission Letter confirms your eligibility to study in International European University. Please note that additional expenses are possible on arrival.",
  firstYearPaymentConfirmation:
    "To continue the admission process and to enroll to the university, you need to cover the complete first year fee. After the payment is received you will be officially accepted to the university and provided with your personal Student\u2019s Office. There you will have an access to your personalized curriculum, all the educational materials, lectures, textbooks, etc. The classes will be provided according to your schedule and curriculum.",
};
